import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Stack,
  Tooltip,
  Collapse,
  IconButton,
  Alert,
  CircularProgress
} from '@mui/material';
// Path imports, not the @mui/icons-material barrel: the barrel is a 3+ s
// cold load (see EmailPreviewDialog).
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { alpha } from '@mui/material/styles';
import { RemarkSuggestion } from '../../../hooks/useRemarkSuggestions';

interface RemarkSuggestionsProps {
  suggestions: RemarkSuggestion[];
  loading?: boolean;
  error?: string | null;
  onSelectSuggestion: (suggestion: RemarkSuggestion) => void;
  compact?: boolean;
}

const RemarkSuggestions: React.FC<RemarkSuggestionsProps> = ({
  suggestions,
  loading = false,
  error = null,
  onSelectSuggestion,
  compact = false
}) => {
  const [expanded, setExpanded] = React.useState(!compact);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1 }}>
        <CircularProgress size={16} />
        <Typography variant="caption" color="text.secondary">
          Finding suggested remarks...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 1 }}>
        {error}
      </Alert>
    );
  }

  if (suggestions.length === 0) {
    return null; // Don't show anything if no suggestions
  }

  const topSuggestion = suggestions[0];
  const otherSuggestions = suggestions.slice(1);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        border: '1px solid',
        borderColor: 'primary.light',
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
        p: compact ? 1 : 1.5,
        mb: 1
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          <Typography variant="subtitle2" color="primary" fontWeight={600}>
            Suggested Remarks ({suggestions.length})
          </Typography>
        </Box>
        
        {compact && otherSuggestions.length > 0 && (
          <IconButton 
            size="small" 
            onClick={() => setExpanded(!expanded)}
            sx={{ ml: 1 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        )}
      </Box>

      {/* Top Suggestion - Always Visible */}
      <Box sx={{ mb: otherSuggestions.length > 0 ? 1 : 0 }}>
        <SuggestionCard
          suggestion={topSuggestion}
          onSelect={onSelectSuggestion}
          isTopSuggestion
          compact={compact}
        />
      </Box>

      {/* Other Suggestions - Collapsible in compact mode */}
      {otherSuggestions.length > 0 && (
        <Collapse in={expanded}>
          <Stack spacing={1}>
            {otherSuggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onSelect={onSelectSuggestion}
                compact={compact}
              />
            ))}
          </Stack>
        </Collapse>
      )}
    </Paper>
  );
};

interface SuggestionCardProps {
  suggestion: RemarkSuggestion;
  onSelect: (suggestion: RemarkSuggestion) => void;
  isTopSuggestion?: boolean;
  compact?: boolean;
}

const SuggestionCard: React.FC<SuggestionCardProps> = ({
  suggestion,
  onSelect,
  isTopSuggestion = false,
  compact = false
}) => {
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return 'success';
    if (confidence >= 50) return 'warning';
    return 'default';
  };

  const getConfidenceLabel = (confidence: number) => {
    if (confidence >= 80) return 'High Match';
    if (confidence >= 50) return 'Good Match';
    return 'Possible Match';
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1 : 1.5,
        border: '1px solid',
        borderColor: isTopSuggestion ? 'primary.main' : 'divider',
        bgcolor: isTopSuggestion ? 'background.paper' : 'grey.50',
        position: 'relative'
      }}
    >
      {/* Title and Usage Count */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }}>
          {suggestion.title}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
          {isTopSuggestion && (
            <Tooltip title="Best match for this item">
              <Chip 
                icon={<CheckIcon />}
                label="Best Match" 
                size="small" 
                color="primary" 
                variant="filled"
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
            </Tooltip>
          )}
          
          {suggestion.usageCount > 0 && (
            <Tooltip title={`Used ${suggestion.usageCount} times`}>
              <Chip
                icon={<TrendingUpIcon />}
                label={`${suggestion.usageCount}x`}
                size="small"
                color="default"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
            </Tooltip>
          )}
          
          <Chip
            label={getConfidenceLabel(suggestion.confidence)}
            size="small"
            color={getConfidenceColor(suggestion.confidence)}
            sx={{ height: 22, fontSize: '0.75rem' }}
          />
        </Box>
      </Box>

      {/* Content Preview */}
      <Typography 
        variant="body2" 
        color="text.secondary"
        sx={{ 
          mb: 1,
          display: '-webkit-box',
          WebkitLineClamp: compact ? 2 : 3,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          fontSize: compact ? '0.75rem' : '0.875rem'
        }}
      >
        {suggestion.content}
      </Typography>

      {/* Match Reasons */}
      {!compact && suggestion.matchReasons.length > 0 && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap">
            {suggestion.matchReasons.map((reason, index) => (
              <Chip
                key={index}
                label={reason}
                size="small"
                variant="outlined"
                sx={{ 
                  height: 20,
                  fontSize: '0.75rem',
                  '& .MuiChip-label': { px: 0.75 }
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Price and Action */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {suggestion.price && (
          <Typography variant="body2" fontWeight={600} color="success.main">
            ${suggestion.price.toFixed(2)}
          </Typography>
        )}
        
        <Button
          size="small"
          variant={isTopSuggestion ? 'contained' : 'outlined'}
          onClick={() => onSelect(suggestion)}
          sx={{ ml: 'auto' }}
        >
          Use This
        </Button>
      </Box>
    </Paper>
  );
};

export default RemarkSuggestions;

